import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { LanguageSelector } from './components/LanguageSelector';
import { Footer } from './components/Footer';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-900 flex flex-col relative overflow-x-hidden selection:bg-cyan-200 selection:text-cyan-900">
      {/* Navbar */}
      <nav className="w-full py-6 px-8 flex justify-between items-center border-b border-slate-200/60 bg-white/70 backdrop-blur-md sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-500/20 group-hover:shadow-cyan-500/40 transition-shadow">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold font-display tracking-tight text-slate-800">SlideDeck<span className="text-cyan-600">Cleaner</span></span>
          </Link>
        </div>
        
        <LanguageSelector />
      </nav>

      <main className="flex-1 max-w-4xl mx-auto p-6 md:p-12 w-full z-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 text-slate-900">Privacy Policy</h1>
        
        <div className="prose prose-slate max-w-none space-y-8 text-slate-600">
          <p className="text-sm text-slate-500">Last updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">1. Introduction</h2>
            <p>
              Welcome to SlideDeckCleaner. We respect your privacy and are committed to protecting your personal data. 
              This privacy policy will inform you about how we look after your personal data when you visit our website, 
              use our services (including paid features), and tell you about your privacy rights.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">2. Information We Collect</h2>
            <p>
              To provide our services, we may collect the following types of information:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <strong>Personal Information:</strong> When you purchase credits or contact support, we may collect your email address 
                to identify your account, deliver receipts, and provide customer service.
              </li>
              <li>
                <strong>Usage Data:</strong> We track usage metrics (such as page conversion counts) to manage your credit balance 
                and ensure the stability of our service.
              </li>
              <li>
                <strong>Uploaded Files:</strong> The PDF files you upload are used <strong>solely</strong> for the purpose of processing.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">3. Payment Information</h2>
            <p>
              We provide paid products and/or services within the Service. In that case, we use third-party services for payment processing (e.g., payment processors).
            </p>
            <p className="mt-2">
              We <strong>do not</strong> store or collect your payment card details. That information is provided directly to our third-party payment processors 
              whose use of your personal information is governed by their Privacy Policy. These payment processors adhere to the standards set by PCI-DSS 
              as managed by the PCI Security Standards Council.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">4. File Security & Retention</h2>
            <p>
              Your document security is our top priority:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <strong>Temporary Storage:</strong> Uploaded files and processed results are stored temporarily on secure servers solely for the duration of the processing.
              </li>
              <li>
                <strong>Automatic Deletion:</strong> All user files are automatically and permanently deleted from our servers within <strong>1 hour</strong> after processing. 
                We do not keep backups of your uploaded content.
              </li>
              <li>
                <strong>No Data Mining:</strong> We do not analyze, scan, or use the content of your documents for training AI models or marketing purposes.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">5. Cookies and Local Storage</h2>
            <p>
              We use cookies and local storage to help us remember your preferences (such as language settings) and to facilitate 
              the operation of our service (such as maintaining your login session). We may also use anonymous analytics tools 
              to help us improve our website performance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">6. Changes to This Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new 
              Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">7. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or your transaction history, please contact us through our website support channels.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;